import { createSelector } from "@reduxjs/toolkit";

import type { Effect as RuntimeEffect, ITrigger as RuntimeTrigger } from "@skilitics/nautilus-core";
import {
  assert,
  getConditionBuildSelector,
  handlersByIdSelector,
  queryEffectBuildsSelector,
  responsesByIdSelector,
  triggerIdsStateSelector,
  triggersByIdSelector,
  type EffectBuild,
} from "@skilitics/nautilus-core-designer-data";

import type { GetEffectBuildable } from "../../triggers/index.ts";
import type { BuildRuntimeCondition } from "./buildRuntimeCondition.ts";
import type { BuildRuntimeEffect } from "./buildRuntimeEffect.ts";
import type { BuildTriggerWhereCondition } from "./buildTriggerWhereCondition.ts";

const isEffect = (b?: RuntimeEffect): b is RuntimeEffect => b !== undefined;

/**
 * STOPGAP for #1414, remove with #1413 (see `EffectBuilder.terminal`).
 * Terminal effects go last, relative order otherwise preserved.
 */
const orderTerminalEffectsLast = (
  effectBuilds: readonly EffectBuild[],
  getEffectBuildable: GetEffectBuildable,
): EffectBuild[] => {
  const isTerminal = ({ nodeId, buildableName }: EffectBuild) =>
    getEffectBuildable({ nodeId, name: buildableName })?.builder.terminal === true;
  return [...effectBuilds.filter((b) => !isTerminal(b)), ...effectBuilds.filter(isTerminal)];
};

export const runtimeTriggersDataSelector = createSelector(
  [
    getConditionBuildSelector,
    queryEffectBuildsSelector,
    triggerIdsStateSelector,
    responsesByIdSelector,
    triggersByIdSelector,
    handlersByIdSelector,
  ],
  (getConditionBuild, queryEffectBuilds, triggerIdsState, responsesById, triggersById, handlersById) => ({
    getConditionBuild,
    queryEffectBuilds,
    triggerIdsState,
    responsesById,
    triggersById,
    handlersById,
  }),
);

interface Options extends ReturnType<typeof runtimeTriggersDataSelector> {
  getEffectBuildable: GetEffectBuildable;
  buildRuntimeEffect: BuildRuntimeEffect;
  buildRuntimeCondition: BuildRuntimeCondition;
  buildTriggerWhereCondition: BuildTriggerWhereCondition;
}

export const getRuntimeTriggersCreate = ({
  getEffectBuildable,
  buildRuntimeEffect,
  buildRuntimeCondition,
  buildTriggerWhereCondition,
  getConditionBuild,
  queryEffectBuilds,
  triggersById,
  handlersById,
  responsesById,
  triggerIdsState: { triggerIdsByNodeId, handlerIdsByTriggerId, responseIdsByHandlerId },
}: Options) => {
  return (nodeId: number) => {
    return (triggerIdsByNodeId[nodeId] ?? []).map((triggerId): RuntimeTrigger<any> => {
      const { cases: triggerCases } = triggersById[triggerId];
      const handlerIds = handlerIdsByTriggerId[triggerId] ?? [];

      const handlers = handlerIds.map((handlerId) => {
        const handler = handlersById[handlerId];
        const responseIds = responseIdsByHandlerId[handlerId] ?? [];
        const responses = responseIds.map((responseId) => {
          const response = responsesById[responseId];
          assert(response, `Response listed but no definition found with id: ${responseId}`);

          const rootCondition = getConditionBuild(responseId);
          const condition = rootCondition ? buildRuntimeCondition(rootCondition) : undefined;
          const effectBuilds = queryEffectBuilds({ type: "byResponseId", responseId });
          const effects = orderTerminalEffectsLast(effectBuilds, getEffectBuildable)
            .map(buildRuntimeEffect)
            .filter(isEffect);
          return {
            id: responseId,
            label: response.label,
            condition,
            outcome: {
              effects,
            },
          };
        });

        return {
          responses,
          cases: handler?.cases?.map((c) => ({
            event: c.eventType,
            where: c.where ? buildTriggerWhereCondition(nodeId, c.eventType, c.where.config) : undefined,
          })),
        };
      });

      return {
        cases: triggerCases.map((triggerCase) => {
          const { eventType, where: triggerWhere } = triggerCase;
          const where = buildTriggerWhereCondition(nodeId, eventType, triggerWhere?.config ?? {});
          return { event: eventType, tense: "on" as const, where };
        }),
        handlers,
      };
    });
  };
};

export type GetRuntimeTriggers = ReturnType<typeof getRuntimeTriggersCreate>;
